import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import MapIcon from "@mui/icons-material/Map";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Modal,
  Paper,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Confirm } from "notiflix";
import CloseIcon from "@mui/icons-material/Close";
import AppContext from "../../../../Contexts/AppContext";
import ClearIcon from "@mui/icons-material/Clear";
import {
  changeCategory,
  getCarbonDailyQuotes,
  getCarbonHourlyQuotes,
  getCarbonQuotes,
  processArea,
  processAreaSpain,
  processAreaPortugal,
  downloadCarbonSinkDoc,
} from "../../../../../api/ApiCarbonSink";
import EuroIcon from "@mui/icons-material/Euro";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip as MUITooltip,
} from "@mui/material";
import Co2Icon from "@mui/icons-material/Co2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ArcElement,
  Title,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
} from "chart.js";
import { Radar, Pie, PolarArea, Line, Scatter } from "react-chartjs-2";
import { m } from "framer-motion";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { toast } from "react-toastify";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { resetDrawingSession } from "../../../../../services/MapCore/MapService";
import ForestIcon from "@mui/icons-material/Forest";
import { Bar } from "react-chartjs-2";
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ArcElement,
  Title,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement
);

const units = {
  area_ha: "ha",
  area_km2: "km²",
  area_m2: "m²",
  carbon_credits_diff_tCO2e_km2: "tCO₂e/km²",
  carbon_credits_difference_tCO2e: "tCO₂e",
  carbon_credits_tCO2e_per_km2: "tCO₂e/km²",
  carbon_credits_tCO2e: "tCO₂e",
  carbon_diff_Mg_per_km2: "mg/km²",
  carbon_Mg_per_km2: "Mg/km²",
  carbon_Mg: "Mg",
  market_value_diff_euros_per_km2: "€/km²",
  market_value_difference_euros: "€",
  market_value_euros_per_km2: "€/km²",
  market_value_euros_per_m2: "€/m²",
  market_value_euros: "€",
  new_land_cover_Mg_per_km2: "Mg/km²",
  // carbon_quote: "€/CC",
  actual_carbon_quote: "€/CC",
};

const categoriesColors_raw = {
  CARBON_SINK: {
    // "Sin datos": null,
    "Cobertura arbórea": "#31a354",
    Matorral: "#addd8e",
    Pastizales: null,
    "Tierras de cultivo": "#fd8d3c",
    "Área urbanizada": "#e34a33",
    "Vegetación escasa o desnuda": "#fef0d9",
    "Cuerpos de agua permanentes": "#3182bd",
    "Humedales herbáceos": "#7fcdbb",
    Manglares: null,
    "Musgos y líquenes": null,
  },
  CARBON_SINK_PT: {
    // PT
    "Agricultura com espaços naturais e seminaturais": "#1ff057",
    "Agricultura protegida e viveiros": "#f0e18c",
    "Albufeiras de barragens": "#75eb4b",
    "Áreas em construção": "#cf5820",
    Aterros: "#b5d137",
    Comércio: "#e03e67",
    "Culturas temporárias de sequeiro e regadio": "#bda06a",
    "Cursos de água naturais": "#1e8bd3",
    "Equipamentos culturais": "#d80eac",
    "Equipamentos de lazer": "#b44be4",
    "Florestas de castanheiro": "#adda5f",
    "Florestas de espécies invasoras": "#3cd439",
    "Florestas de eucalipto": "#e775e5",
    "Florestas de outras folhosas": "#3176dd",
    "Florestas de outras resinosas": "#8b56c8",
    "Florestas de outros carvalhos": "#23bec9",
    "Florestas de pinheiro bravo": "#666dce",
    "Florestas de sobreiro": "#56d88d",
    Indústria: "#f03a26", // DONE
    "Infraestruturas de produção de energia renovável": "#b35a2a",
    "Infraestruturas de tratamento de resíduos e águas residuais": "#13eaca",
    "Infraestruturas para captação, tratamento e abastecimento de águas para consumo":
      "#565dd8",
    "Instalações agrícolas": "#56d37e",
    "Instalações desportivas": "#ff7f00",
    "Lixeiras e Sucatas": "#ffb674",
    Matos: "#56c453",
    "Mosaicos culturais e parcelares complexos": "#8824a3",
    Olivais: "#6fd738",
    "Outros equipamentos e instalações turísticas": "#d39c1b",
    "Parques e jardins": "#4daf4a",
    "Pastagens espontâneas": "#ee6a52",
    "Pastagens melhoradas": "#aa32d6",
    Pedreiras: "#e86298",
    Pomares: "#4f2ee5",
    "Rede viária e espaços associados": "#9f978c",
    "Tecido edificado contínuo predominantemente horizontal": "#8caff0",
    "Tecido edificado contínuo predominantemente vertical": "#ba83ed",
    "Tecido edificado descontínuo": "#6ce3f0",
    "Tecido edificado descontínuo esparso": "#5a5cdc",
    "Vegetação esparsa": "#2add90",
    Vinhas: "#c2de22",
  },
  // SP
  // Arbolado: "#d148fa",
  // "Balsas y estanques": "#38df38",
  // "Caminos y sendas": "#94bc94",
  // Carreteras: "#eec21f",
  // "Coberturas de agua": "#37d3dc",
  // Coníferas: "#d7e8bb",
  // "Construcción deportiva": "#8d910a",
  // Cultivos: "#af378d",
  // "Cultivos herbáceos": "#aca0d9",
  // "Cursos de agua": "#dc602f",
  // Desconocido: "#4af5f4",
  // Edificación: "#9f9053",
  // Embalses: "#8d494e",
  // "Frondosas caducifolias": "#c7a4e6",
  // "Frondosas perennifolias": "#12b3ba",
  // "Frutales no cítricos": "#09d933",
  // Huerta: "#7b3c29",
  // "Otras construcciones": "#8de71c",
  // Pastizal: "#b07a24",
  // "Pastizal-matorral": "#029890",
  // "Pasto arbolado": "#b9f6dc",
  // Piscina: "#39a40e",
  // Prados: "#cb9dec",
  // "Red de transporte terrestre sin catalogar": "#fa51b4",
  // "Suelo no edificado": "#e8b704",
  // "Terrenos con escasa o nula vegetación": "#5b6d32",
  // "Vías urbanas": "#030cdc",
  // Viñedo: "#5be2ae",
  // "Zona abierta": "#294119",
  // "Zonas de extracción": "#b9d843",
  // "Zonas de vertido": "#51ec68",
  // "Zonas pavimentadas o selladas": "#947d76",
  // "Zona verde artificial y arbolado urbano": "#867dea",
  CARBON_SINK_SP: {
    "Abedul pubescens (Betula alba)": "#3cb371",
    "Acebo (Ilex aquifolium)": "#8b4513",
    "Agrícola con arbolado": "#ffffe0",
    "Aliso (Alnus glutinosa)": "#dc143c",
    Arbolado: "#d148fa",
    "Arce seudoplátano (Acer pseudoplatanus)": "#a9a9a9",
    "Avellano (Corylus avellana)": "#ff0000",
    "Balsas y estanques": "#38df38",
    "Bardaguera (Salix atrocinerea)": "#e6e6fa",
    "Caminos y sendas": "#94bc94",
    Carreteras: "#eec21f",
    "Castaño (Castanea sativa)": "#4b0082",
    "Coberturas de agua": "#37d3dc",
    "Construcción deportiva": "#8d910a",
    Coníferas: "#d7e8bb",
    "Coníferas genéricas": "#dda0dd",
    Cultivos: "#af378d",
    "Cultivos herbáceos": "#aca0d9",
    "Cursos de agua": "#dc602f",
    Desconocido: "#4af5f4",
    Edificación: "#9f9053",
    Embalses: "#8d494e",
    "Eucalipto globulus (Eucalyptus globulus)": "#d8bfd8",
    "Eucalipto nitens (Eucalyptus nitens)": "#e9967a",
    "Fresno excelsior (Fraxinus excelsior)": "#b8860b",
    "Frondosas caducifolias": "#c7a4e6",
    "Frondosas genéricas": "#faf0e6",
    "Frondosas perennifolias": "#12b3ba",
    "Frutales no cítricos": "#09d933",
    "Haya (Fagus sylvatica)": "#00ffff",
    Huerta: "#7b3c29",
    "Madroño (Arbutus unedo )": "#b0c4de",
    Matorral: "#800000",
    "Otras construcciones": "#8de71c",
    Pastizal: "#a9a9a9",
    "Pastizal-matorral": "#029890",
    "Pasto arbolado": "#b9f6dc",
    "Peral silvestre (Pyrus spp.": "#00ffff",
    "Pino pináster (Pinus pinaster)": "#000080",
    "Pino radiata (Pinus radiata)": "#191970",
    "Pino silvestre (Pinus sylvestris)": "#483d8b",
    Piscina: "#39a40e",
    Prados: "#cb9dec",
    "Rebollo (Quercus pyrenaica)": "#ffffff",
    "Red de transporte terrestre sin catalogar": "#fa51b4",
    "Roble (Quercus petraea)": "#ba55d3",
    "Roble pedunculado (Quercus robur)": "#ffefd5",
    "Serbal de cazadores (Sorbus aucuparia)": "#f5f5dc",
    "Seudotsuga (Pseudotsuga menziesii)": "#daa520",
    "Suelo no edificado": "#e8b704",
    "Terrenos con escasa o nula vegetación": "#5b6d32",
    Viñedo: "#5be2ae",
    "Vías urbanas": "#030cdc",
    "Zona abierta": "#294119",
    "Zona verde artificial y arbolado urbano": "#867dea",
    "Zonas de extracción": "#b9d843",
    "Zonas de vertido": "#51ec68",
    "Zonas pavimentadas o selladas": "#947d76",
  },
};

export const CarbonFeatureFormPt = forwardRef(({ onClose }, ref) => {
  // console.log("FeatureForm content", content, content[0]?.view?.[0]);
  const {
    // mapInstanceRef,
    editedGeometry,
    setEditedGeometry,
    layersGroupsRef,
    modeOptions,
    carbonResponse,
    setCarbonResponse,
    actualGroundUse,
    setActualGroundUse,
    carbonCategoryRef,
    mapInstanceRef,
    project,
  } = useContext(AppContext);

  console.log("layersGroupsRef", layersGroupsRef?.current);
  const { t, i18n } = useTranslation("main");

  const [areaUnit, setAreaUnit] = useState("%");
  // const [areaUnit, setAreaUnit] = useState("m2");
  const [carbonQuotes, setCarbonQuotes] = useState();
  const [fullCarbonResponse, setFullCarbonResponse] = useState({});
  // const handleChangeAreaUnit = (event, newAlignment) => {
  //   if (!newAlignment) return;
  //   setAreaUnit(newAlignment);
  // };
  const categoriesColors =
    { ...categoriesColors_raw[project?.category], "Sin datos": null } || {};
  // const initialCategory = Object.keys(categoriesColors)[0];
  const initialCategory = "Sin datos";

  const [category, setCategory] = useState(initialCategory);
  // const [response, setResponse] = useState({});
  // const [actualGroundUse, setActualGroundUse] = useState({});
  // console.log("actualGroundUse", actualGroundUse);
  const [isLoading, setIsLoading] = useState(false);

  // const [showMore, setShowMore] = useState({
  //   area: true,
  //   market_value: true,
  //   carbon_credits: true,
  // });

  // const toggleSection = (section) => {
  //   setShowMore((prev) => ({
  //     ...prev,
  //     [section]: !prev[section],
  //   }));
  // };

  // const getMaxHeight = (section) => (showMore[section] ? "300px" : "0px");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getCarbonDailyQuotes();
        // getCarbonHourlyQuotes();
        console.log("carbon quotes", res);
        setCarbonQuotes(
          res
            .sort((a, b) => new Date(b.fx_value) - new Date(a.fx_value)) // orden descendente (más nuevo primero)
            .slice(0, 30) // tomamos los 30 más recientes
            .sort((a, b) => new Date(a.fx_value) - new Date(b.fx_value)) // los reordenamos de más antiguo a más nuevo
        );
      } catch (error) {
        console.error("Error al cargar los valores de carbono:", error);
      }
    };
    fetchData();
  }, []);

  console.log("modeOptions.CARBON_SINK[0]", modeOptions);

  useEffect(() => {
    // console.log("editedGeometry", editedGeometry);
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const geometry = {
          coordinates: editedGeometry,
          type: "Polygon",
          // type: "MultiPolygon",
        };
        const services = {
          CARBON_SINK: processArea,
          CARBON_SINK_SP: processAreaSpain,
          CARBON_SINK_PT: processAreaPortugal,
        };
        const res = await services[project?.category](
          modeOptions.CARBON_SINK[0],
          geometry
        );
        // console.log("RESPONSE PORTUGALLLLL", typeof res);
        // console.log(
        //   "RESPONSE PORTUGAL 1",
        //   res["Florestas de outros carvalhos"]
        //   // [
        //   //   "Absorción acumulada total (tCO₂)"
        //   // ]
        // );
        // console.log(
        //   "RESPONSE PORTUGAL",
        //   res["Florestas de outros carvalhos"]["1"]
        //   // [
        //   //   "Absorción acumulada total (tCO₂)"
        //   // ]
        // );
        // console.log("RESPONSEEE", res);
        setCarbonResponse(res.classes);
        setFullCarbonResponse(res);
        // function calcularDistribucionArea(clases) {
        //   const areas_ha = {};

        //   // Extraer el area_ha de cada clase (primer año válido)
        //   for (const [clase, años] of Object.entries(clases)) {
        //     const area = Object.values(años).find(
        //       (a) => a?.area_ha != null
        //     )?.area_ha;
        //     if (area != null) {
        //       areas_ha[clase] = area;
        //     }
        //   }

        //   const total_ha = Object.values(areas_ha).reduce(
        //     (acc, val) => acc + val,
        //     0
        //   );

        //   const distribucion = {
        //     "%": {},
        //     ha: {},
        //     m2: {},
        //     km2: {},
        //   };

        //   for (const [clase, area_ha] of Object.entries(areas_ha)) {
        //     distribucion["%"][clase] = area_ha / total_ha;
        //     distribucion["ha"][clase] = area_ha;
        //     distribucion["m2"][clase] = area_ha * 10_000;
        //     distribucion["km2"][clase] = area_ha / 100;
        //   }

        //   return distribucion;
        // }
        // console.log("CALCU", calcularDistribucionArea(res));
        // setActualGroundUse(calcularDistribucionArea(res));
        // setActualGroundUse({ "%": calcularPorcentajePorClase(res) });
        setActualGroundUse(res.ground_use_distribution);
        // setIsLoading(false);

        setAreaUnit(
          res.area_km2 > 1 ? "km2" : res.area_km2 * 100 > 1 ? "ha" : "m2"
        );
      } catch (error) {
        console.error("Error al procesar el área:", error);
        // Opcional: puedes mostrar un mensaje al usuario
        setCarbonResponse({});
        setActualGroundUse({});
        setEditedGeometry(null);
        toast.error(t("geometry_error"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          // transition: Bounce,
        });
        onClose();
      } finally {
        setIsLoading(false);
      }
    };
    if (!editedGeometry) return;
    fetchData();
  }, [editedGeometry]);

  const handleChangeCategory = async (e) => {
    const newCategory = e?.target?.value;
    setCategory(newCategory);
    setIsLoading(true);
    const geometry = {
      coordinates: editedGeometry,
      type: "Polygon",
      // type: "MultiPolygon",
    };

    const res = await changeCategory(
      modeOptions.CARBON_SINK[0],
      geometry,
      newCategory
    );

    setCarbonResponse(res);
    setIsLoading(false);
  };
  console.log("carbonResponse", carbonResponse);
  const valuesOriginal = [
    carbonResponse?.carbon_credits_tCO2e ?? 0,
    carbonResponse?.market_value_euros ?? 0,
    carbonResponse?.carbon_Mg ?? 0,
  ];

  const valuesComparado = [
    (carbonResponse?.carbon_credits_tCO2e ?? 0) +
      (carbonResponse?.carbon_credits_tCO2e_diff ?? 0),
    (carbonResponse?.market_value_euros ?? 0) + (carbonResponse?.market_value_euros_diff ?? 0),
    (carbonResponse?.carbon_Mg ?? 0) + (carbonResponse?.carbon_Mg_diff ?? 0),
  ];

  const normalizeRelativeToOriginal = (value, base) => {
    if (base === 0) return 0;
    return value / base;
  };

  const normalizedOriginal = [];
  const normalizedComparado = [];

  for (let i = 0; i < valuesOriginal.length; i++) {
    const base = valuesOriginal[i];
    normalizedOriginal.push(
      normalizeRelativeToOriginal(valuesOriginal[i], base)
    ); // será 1
    normalizedComparado.push(
      normalizeRelativeToOriginal(valuesComparado[i], base)
    ); // relativo
  }

  // console.log("ACA valuesOriginal", valuesOriginal);
  // console.log("ACA valuesComparado", valuesComparado);
  // console.log("ACA normalizedOriginal", normalizedOriginal);
  // console.log("ACA normalizedComparado", normalizedComparado);

  function hexToRGBA(hex, alpha = 0.2) {
    if (!hex) return "rgba(0, 0, 0, 0.2)";
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const originalColor = "#6a51a3";
  const originalColorRGBA = hexToRGBA(originalColor, 0.2);

  const colorHex = categoriesColors[category];
  const backgroundRGBA = hexToRGBA(colorHex, 0.2);
  const borderColor = colorHex || "rgb(54, 162, 235)";

  const radarData = {
    labels: [
      t("carbon_credits_tCO2e"),
      t("market_value_euros"),
      t("carbon_Mg"),
    ],
    datasets: [
      {
        label: t("Sin datos"),
        data: normalizedOriginal,
        fill: true,
        backgroundColor: originalColorRGBA,
        borderColor: originalColor,
        pointBackgroundColor: originalColor,
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: originalColor,
      },
      {
        label: t(category),
        data: normalizedComparado,
        fill: true,
        backgroundColor: backgroundRGBA,
        borderColor: borderColor,
        pointBackgroundColor: borderColor,
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: borderColor,
      },
    ],
  };

  // 1. Calcula el valor mínimo entre tus datasets
  const allValues = [...normalizedOriginal, ...normalizedComparado];
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const minValue = Math.min(0, dataMin); // valor mínimo entre 0 y el más pequeño
  const maxValue = Math.max(1, dataMax); // valor mínimo entre 0 y el más pequeño

  const radarOptions = {
    maintainAspectRatio: false,
    // responsive: true,
    plugins: {
      legend: {
        position: "right",
      },
      title: {
        display: true,
        text: t("new_ground_use_normalized_update"),
        color: "#333",
        align: "center", // 'center' o 'end' también válido
        font: {
          size: 14,
          weight: "bold",
        },
        // color: "#333",
        padding: {
          top: 10,
          bottom: 10,
        },
      },
    },
    scales: {
      r: {
        beginAtZero: false, // esto lo ignoramos porque vamos a poner el valor exacto
        min: minValue,
        max: maxValue, // ajusta si quieres un tope superior, o usa Math.max(...allValues)
        ticks: {
          stepSize: 0.2, // opcional: para control más fino de los ticks
        },
      },
    },
  };

  const groundUseLabels = Object.keys(actualGroundUse?.["%"] ?? []);
  console.log("dataaa", actualGroundUse);
  // console.log("actualGroundUse?.[ % ]", actualGroundUse?.["%"]);

  const groundUseData = {
    labels: groundUseLabels.map(
      (label) =>
        `${(actualGroundUse?.["%"][label] * 100).toFixed(0)}% - ${t(label)}`
    ),
    datasets: [
      {
        label: t(areaUnit),
        data: Object.values(actualGroundUse?.[areaUnit] ?? []),
        backgroundColor: groundUseLabels.map((label) =>
          categoriesColors[label] ? `${categoriesColors[label]}4D` : "#c8c8c84D"
        ),
        borderColor: groundUseLabels.map(
          (label) => categoriesColors[label] ?? "rgba(200,200,200,1)"
        ),
        borderWidth: 1,
      },
    ],
  };

  const groundUseOptions = {
    maintainAspectRatio: false,
    // responsive: true,
    plugins: {
      title: {
        display: true,
        text: t("actual_ground_use_distribution"),
        color: "#333",
        align: "center", // 'center' o 'end' también válido
        font: {
          size: 14,
          weight: "bold",
        },
        // color: "#333",
        padding: {
          top: 10,
          bottom: 10,
        },
      },
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 50,
          padding: 15,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    // Puedes agregar layout para más control del espacio
    // layout: {
    //   padding: {
    //     right: 20,
    //   },
    // },
  };

  const handleCloseForm = () => {
    resetDrawingSession({ map: mapInstanceRef.current, setEditedGeometry });
    onClose();
  };

  useImperativeHandle(ref, () => ({
    function: handleCloseForm, // Evita la recursión
  }));

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: t("carbon_quotes_graph_title"),
      },
    },
  };

  // const labels = [
  //   "January",
  //   "February",
  //   "March",
  //   "April",
  //   "May",
  //   "June",
  //   "July",
  // ];
  // const lineLabels = carbonQuotes?.map((el) => {
  //   const date = new Date(el.fx_value);
  //   const day = String(date.getDate()).padStart(2, "0"); // Añade cero si es menor de 10
  //   const month = String(date.getMonth() + 1).padStart(2, "0"); // getMonth es 0-indexado
  //   return `${day}-${month}`;
  // });
  const lineLabels = carbonQuotes?.map((el) => {
    const date = new Date(el.fx_value);
    const day = String(date.getDate()).padStart(2, "0");

    // Asume que tienes disponible la variable `i18n.language` con 'es', 'en', o 'pt'
    const monthShort = new Intl.DateTimeFormat(i18n.language, {
      month: "short",
    }).format(date);

    return `${day}-${monthShort}`;
  });

  const lineData = {
    labels: lineLabels,
    datasets: [
      {
        label: t("carbon_quote"),
        data: carbonQuotes?.map((el) => el.price_value),
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
      },
    ],
  };

  const carbonResponseClassFiltered = Object.entries(carbonResponse ?? {}).filter(
    ([_, speciesDict]) => {
      const hasValues = Object.values(speciesDict).some((yearDict) => {
        const parameters = [
          "Absorción acumulada (tCO2/ha)",
          "Absorción acumulada (tCO2/pie)",
          "Absorción acumulada total (tCO₂)",
          "carbon_credits_tCO2e_acumulados_constante",
          "carbon_credits_tCO2e_acumulados_optimista",
          "carbon_credits_tCO2e_acumulados_pesimista",
        ];
        return (
          typeof yearDict === "object" &&
          parameters.every(
            (param) => yearDict[param] != null && yearDict[param] !== 0
          )
          // yearDict.carbon_credits_tCO2e_acumulados_constante != null &&
          // yearDict.carbon_credits_tCO2e_acumulados_constante != 0
        );
      });
      return hasValues;
    }
  );

  const carbonCreditsPerYearByClassesData = {
    labels: Array.from({ length: 40 }, (_, i) => i + 1),
    datasets: carbonResponseClassFiltered.map(([speciesName, speciesDict]) => {
      return {
        label: t(speciesName),
        data: Object.entries(speciesDict).map(([_, yearDict]) => {
          return yearDict.carbon_credits_tCO2e_acumulados_constante > 0
            ? yearDict.carbon_credits_tCO2e_acumulados_constante
            : 0.0001;
        }),
        borderColor: categoriesColors[speciesName],
        backgroundColor: `${categoriesColors[speciesName]}55`,
      };
    }),
  };

  const pieHaRelationData = {
    // labels: carbonResponseClassFilter.map(([speciesName, speciesDict]) => {
    //   return Object.entries(speciesDict).map(([_, yearDict]) => {
    //     return yearDict["Absorción acumulada (tCO2/ha)"];
    //   });
    // }),
    datasets: carbonResponseClassFiltered.map(([speciesName, speciesDict]) => {
      return {
        label: t(speciesName),
        data: Object.entries(speciesDict).map(([_, yearDict]) => {
          return {
            x: yearDict["Absorción acumulada (tCO2/pie)"],
            y: yearDict["Absorción acumulada (tCO2/ha)"],
          };
          // return yearDict["Absorción acumulada (tCO2/ha)"] > 0
          //   ? yearDict["Absorción acumulada (tCO2/ha)"]
          //   : 0.0001;
        }),
        borderColor: categoriesColors[speciesName],
        backgroundColor: `${categoriesColors[speciesName]}55`,
      };
    }),
  };

  // console.log("pieHaRelation", pieHaRelationData);

  const lineOptions2 = {
    maintainAspectRatio: false, // 👈 necesario para que tome la altura del contenedor
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
        text: t("species_per_year_graph_title2"),
      },
    },
    scales: {
      y: {
        type: "logarithmic",
        title: {
          display: true,
          text: t("accumulated_carbon_credits"),
          // text: "€",
        },
        ticks: {
          callback: function (value, index, values) {
            return Number(value).toLocaleString(); // Opcional: formatea valores
          },
        },
        min: 0.0001, // evita error por log(0)
      },
      x: {
        title: {
          display: true,
          text: t("year"),
        },
      },
    },
  };

  const scatterOptions = {
    // scales: {
    //   y: {
    //     beginAtZero: true,
    //   },
    // },
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: t("relation_tCO2/pie_tCO2/ha"),
      },
    },
    scales: {
      y: {
        // type: "logarithmic",
        title: {
          display: true,
          text: "€ Carbon credits acumulados",
          // text: "€",
        },
        ticks: {
          callback: function (value, index, values) {
            return Number(value).toLocaleString(); // Opcional: formatea valores
          },
        },
        min: 0.0001, // evita error por log(0)
      },
      x: {
        title: {
          display: true,
          text: t("year"),
        },
      },
    },
  };

  // console.log(
  //   "clases fullCarbonResponse?.df_modelado_acumulado_actual",
  //   fullCarbonResponse?.df_modelado_acumulado_actual
  // );

  // const clasesUnicas = useMemo(() => {
  //   const set = new Set(
  //     fullCarbonResponse?.df_modelado_desde_0_km2?.map((row) => row.Clase)
  //   );
  //   return Array.from(set).sort();
  // }, [fullCarbonResponse]);

  // const [selectedClase, setSelectedClase] = useState(clasesUnicas[0]);

  // const handleChange = (e) => {
  //   setSelectedClase(e.target.value);
  // };
  console.log(
    "options",
    lineOptions2,
    "data",
    carbonCreditsPerYearByClassesData
  );

  return (
    <Box
      sx={{
        width: "100%",
        padding: "1rem",
        paddingBottom: "0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <div
        style={{
          flexGrow: "1",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          justifyContent: isLoading ? "center" : "flex-start",
        }}
      >
        {isLoading ? (
          <CircularProgress style={{ alignSelf: "center" }} />
        ) : (
          <>
            {!["CARBON_SINK_PT", "CARBON_SINK_SP"].includes(
              project.category
            ) && (
              <div ref={carbonCategoryRef}>
                <TextField
                  value={category ?? ""}
                  onChange={handleChangeCategory}
                  label={t("category")}
                  fullWidth
                  select
                  variant="outlined"
                >
                  {Object.entries(categoriesColors).map(([option, color]) => (
                    <MenuItem key={option} value={option}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {option === "Sin datos" ? (
                          <div
                            style={{
                              height: "1rem",
                              width: "1rem",
                              display: "grid",
                              gridTemplateColumns: "repeat(3, 1fr)",
                              gridTemplateRows: "repeat(3, 1fr)",
                              borderRadius: "4px",
                              overflow: "hidden",
                              marginRight: "0.5rem",
                            }}
                          >
                            {[
                              "#31a354",
                              "#31a354",
                              "#e34a33",
                              "#3182bd",
                              "#7fcdbb",
                              "#addd8e",
                              "#31a354",
                              "#addd8e",
                              "#e34a33",
                            ].map((c, i) => (
                              <div
                                key={i}
                                style={{
                                  backgroundColor: c,
                                  width: "100%",
                                  height: "100%",
                                }}
                              />
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              height: "1rem",
                              width: "1rem",
                              backgroundColor: color ?? "transparent",
                              border: !color && "1px solid lightgrey",
                              borderRadius: "4px",
                              overflow: "hidden",
                              marginRight: "0.5rem",
                            }}
                          />
                        )}
                        {t(option)}
                      </div>
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            )}
            {/* AREA */}
            <CarbonFeatureSection
              title="area"
              icon={<MapIcon />}
              initialExpanded={false}
            >
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "space-around",
                  margin: "0.4rem",
                }}
              >
                {areaUnit === "m2" && (
                  <CustomTextField
                    field={"area_m2"}
                    value={(carbonResponse?.area_km2 ?? 0) * 1_000_000}
                    readOnly
                  />
                )}
                {areaUnit === "km2" && (
                  <CustomTextField
                    field={"area_km2"}
                    value={carbonResponse?.area_km2 ?? 0}
                    readOnly
                  />
                )}
                {areaUnit === "ha" && (
                  <CustomTextField
                    field={"area_ha"}
                    value={(carbonResponse?.area_km2 ?? 0) * 100}
                    readOnly
                  />
                )}
                {/* <CustomTextField
                    field={`total_area_${areaUnit}`}
                    value={response[`total_area_${areaUnit}`]}
                    readOnly
                  /> */}

                {/* <ToggleButtonGroup
                    color="primary"
                    value={areaUnit}
                    exclusive
                    onChange={handleChangeAreaUnit}
                    aria-label="Platform"
                  >
                    <ToggleButton value="m2">m²</ToggleButton>
                    <ToggleButton value="km2">km²</ToggleButton>
                    <ToggleButton value="ha">ha</ToggleButton>
                  </ToggleButtonGroup> */}
              </div>
              <div style={{ height: "300px", minWidth: "550px" }}>
                <Pie
                  data={groundUseData}
                  options={groundUseOptions}
                  height={200}
                />
              </div>
            </CarbonFeatureSection>
            {/* MARKET VALUE */}
            <CarbonFeatureSection
              title="market_value"
              icon={<ForestIcon />}
              max_height="700px"
              initialExpanded={false}
            >
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "space-around",
                  margin: "0.4rem 0",
                }}
              >
                <InputCard
                  title="actual_carbon_quote"
                  value={carbonQuotes?.[carbonQuotes.length - 1]?.price_value}
                  // diff_value={carbonResponse.market_value_euros_diff}
                />
              </div>
              <Line options={lineOptions} data={lineData} />
            </CarbonFeatureSection>

            {project?.category === "CARBON_SINK_PT" && (
              <>
                <CarbonFeatureSection
                  title="accumulated_carbon_credits"
                  icon={<MapIcon />}
                  initialExpanded={false}
                  // max_height="800px"
                >
                  <div style={{ height: "400px" }}>
                    <Line
                      options={lineOptions2}
                      data={carbonCreditsPerYearByClassesData}
                    />
                  </div>
                  {/* <Line options={lineOptions} data={pieHaRelation} /> */}

                  {/* <div style={{ height: "400px" }}>
                    <Scatter
                      options={scatterOptions}
                      data={pieHaRelationData}
                    />
                  </div> */}
                </CarbonFeatureSection>

                <CarbonFeatureSection
                  title="co2_absoption_evolution"
                  icon={<MapIcon />}
                  initialExpanded={false}
                  max_height={"400px"}
                >
                  {/* <TextField
                    label={t("class")} // "Clase" traducido
                    value={selectedClase}
                    onChange={handleChange}
                    select
                    fullWidth
                    style={{ margin: "1rem 0" }}
                  >
                    {clasesUnicas.map((clase) => (
                      <MenuItem key={clase} value={clase}>
                        {t(clase)}
                      </MenuItem>
                    ))}
                  </TextField> */}
                  <AbsorptionLineChart
                    data={fullCarbonResponse?.df_modelado_anual}
                    // clase={selectedClase}
                  />
                </CarbonFeatureSection>
                {fullCarbonResponse?.df_modelado_anual && (
                  <CarbonFeatureSection
                    title="relation_tCO2/pie_tCO2/km2"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    max_height="400px"
                  >
                    <AbsorptionScatterChart
                      data={fullCarbonResponse?.df_modelado_anual}
                      densidadesBase={
                        fullCarbonResponse?.CONFIG?.densidades_base
                      }
                      colorMap={categoriesColors}
                      // activeClassesData={fullCarbonResponse?.df_creditos}
                      activeClassesData={Object.keys(
                        fullCarbonResponse?.ground_use_distribution["m2"]
                      ).map((clase) => ({ Clase: clase }))}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.resumen_por_horizonte_y_clase && (
                  <CarbonFeatureSection
                    title="accumulated_credits_by_class"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    // max_height="400px"
                    max_height={"400px"}
                  >
                    <CreditScenarioLineChartWithSelector
                      data={fullCarbonResponse?.resumen_por_horizonte_y_clase}
                      colorMap={categoriesColors}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.resumen_por_horizonte_y_clase && (
                  <CarbonFeatureSection
                    title="carbon_credits_per_class"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    max_height="400px"
                    // max_height={"400px"}
                  >
                    <CreditByClassMultiChart
                      data={fullCarbonResponse?.resumen_por_horizonte_y_clase}
                      colorMap={categoriesColors}
                    />
                  </CarbonFeatureSection>
                )}

                <DownloadDocButton country="portugal" lang={i18n.language} />
              </>
            )}
            {project?.category === "CARBON_SINK_SP" && (
              <>
                <CarbonFeatureSection
                  title="absorption_evolution_per_class"
                  icon={<MapIcon />}
                  initialExpanded={false}
                  max_height={"400px"}
                >
                  {/* <TextField
                    label={t("class")} // "Clase" traducido
                    value={selectedClase}
                    onChange={handleChange}
                    select
                    fullWidth
                    style={{ margin: "1rem 0" }}
                  >
                    {clasesUnicas.map((clase) => (
                      <MenuItem key={clase} value={clase}>
                        {t(clase)}
                      </MenuItem>
                    ))}
                  </TextField> */}
                  <AbsorptionLineChart
                    data={fullCarbonResponse?.df_modelado_desde_0_km2}
                    // clase={selectedClase}
                  />
                </CarbonFeatureSection>
                {fullCarbonResponse?.df_acumulado_horizontes && (
                  <CarbonFeatureSection
                    title="accumulated_carbon_by_class"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    max_height="400px"
                  >
                    <AbsorptionMultiLineChart
                      data={fullCarbonResponse?.df_acumulado_horizontes}
                      horizontes={[5, 10, 15, 20, 25, 30, 35, 40]}
                      colorMap={categoriesColors}
                      activeClassesData={fullCarbonResponse?.df_creditos}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.df_modelado_desde_0_km2 && (
                  <CarbonFeatureSection
                    title="relation_tCO2/pie_tCO2/km2"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    max_height="400px"
                  >
                    <AbsorptionScatterChart
                      data={fullCarbonResponse?.df_modelado_desde_0_km2}
                      densidadesBase={
                        fullCarbonResponse?.CONFIG?.densidades_base
                      }
                      colorMap={categoriesColors}
                      activeClassesData={fullCarbonResponse?.df_creditos}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.df_creditos && (
                  <CarbonFeatureSection
                    title="carbon_credit_projection_full_area"
                    icon={<MapIcon />}
                    initialExpanded={false}
                  >
                    <CarbonCreditsProjectionChart
                      data={fullCarbonResponse?.df_creditos}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.df_creditos && (
                  <CarbonFeatureSection
                    title="carbon_credits_per_class"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    max_height={"400px"}
                  >
                    <ClassCreditsHorizonChart
                      data={fullCarbonResponse?.df_creditos}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.df_creditos_km2 && (
                  <CarbonFeatureSection
                    title="accumulated_credits_per_km2"
                    icon={<MapIcon />}
                    initialExpanded={false}
                    max_height={"400px"}
                  >
                    <CreditsPerKm2ByClassChart
                      data={fullCarbonResponse?.df_creditos_km2}
                      colorMap={categoriesColors}
                    />
                  </CarbonFeatureSection>
                )}
                {fullCarbonResponse?.df_modelado_acumulado_actual && (
                  <CarbonFeatureSection
                    title="vegetation_state_distribution"
                    icon={<MapIcon />}
                    initialExpanded={false}
                  >
                    <VegetationStateAreaChart
                      data={fullCarbonResponse?.df_modelado_acumulado_actual}
                    />
                  </CarbonFeatureSection>
                )}
                <DownloadDocButton country="spain" lang={i18n.language} />
              </>
            )}
          </>
        )}
      </div>
    </Box>
  );
});

function CopyableText({ text, copyValue }) {
  const { t } = useTranslation("main");

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // vuelve a "Copiar" después de 2s
    } catch (err) {
      console.error("Error al copiar:", err);
    }
  };

  return (
    <MUITooltip title={copied ? t("copied") : t("copy")} followCursor>
      <div
        onClick={handleCopy}
        style={{
          cursor: "pointer",
        }}
      >
        {text}
      </div>
    </MUITooltip>
  );
}

const CustomTextField = ({
  field,
  value,
  options,
  onChange,
  readOnly,
  link,
  disableClear,
  ...rest
}) => {
  const { t } = useTranslation("main");
  const isSelect = Array.isArray(options) && options.length > 0;

  if (!value) return;

  const val = new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value?.toFixed(2));

  return (
    <TextField
      {...rest}
      value={
        Object.keys(units).includes(field) ? `${val} ${units[field]}` : val
      }
      onChange={(e) => {
        if (onChange) onChange(field)(e);
      }}
      // label={readOnly ? `${t(field)} (${t("read_only")})` : t(field)}
      label={t(field)}
      // fullWidth
      select={isSelect} // Solo es select si hay opciones
      InputProps={{
        endAdornment: val && !readOnly && !disableClear && (
          <InputAdornment
            position="end"
            style={{ marginRight: isSelect ? "20px" : "" }}
          >
            <IconButton
              onClick={() => onChange(field)("")}
              size="small"
              style={{
                width: "0.9rem",
                height: "0.9rem",
              }}
            >
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
      variant={readOnly ? "standard" : "outlined"}
      sx={{
        "& .MuiInputBase-inputAdornedEnd": {
          paddingRight: "0!important", // Cambia esto a la propiedad que quieras modificar
        },
      }}
      multiline
    >
      {isSelect
        ? options.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))
        : null}
    </TextField>
  );
};

export const InputCard = ({ title, value, diff_value, decimals = 2 }) => {
  const { t } = useTranslation("main");

  const principalValue = diff_value ? value + diff_value : value;

  const secondaryValue = diff_value ? diff_value : null;
  // const isMore = secondaryValue > 0;

  const formatNumber = (val) => {
    console.log("formatNumber", val, typeof val);
    return new Intl.NumberFormat("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(val?.toFixed(decimals));
  };

  return (
    <Paper
      elevation={3}
      style={{
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <div style={{ whiteSpace: "nowrap" }}>{t(title)}</div>
      <div
        style={{
          display: "flex",
          fontSize: "1.5rem",
          alignSelf: "flex-end",
          alignItems: "center",
          color: secondaryValue
            ? secondaryValue < 0
              ? "red"
              : secondaryValue > 0
                ? "green"
                : ""
            : "",
          // gap: "1rem",
          whiteSpace: "nowrap",
        }}
      >
        {secondaryValue &&
          (secondaryValue < 0 ? (
            <ArrowDropDownIcon
              style={{
                color: "red",
              }}
            />
          ) : (
            <ArrowDropUpIcon
              style={{
                color: "green",
              }}
            />
          ))}
        {/* <div> */}
        <CopyableText
          text={
            Object.keys(units).includes(title)
              ? `${formatNumber(principalValue)} ${units[title]}`
              : formatNumber(principalValue)
          }
          copyValue={principalValue}
        />
        {/* </div> */}
      </div>
      <div
        style={{
          display: "flex",
          fontSize: "0.75rem",
          alignSelf: "flex-end",
        }}
      >
        {secondaryValue > 0 && "+"}
        <CopyableText
          text={
            secondaryValue
              ? Object.keys(units).includes(title)
                ? `${formatNumber(secondaryValue)} ${units[title]}`
                : formatNumber(secondaryValue)
              : // : "\u00A0"
                ""
          }
          copyValue={secondaryValue}
        />
      </div>
    </Paper>
  );
};

export const CarbonFeatureSection = ({
  title,
  icon,
  children,
  max_height,
  initialExpanded = true,
}) => {
  const { t } = useTranslation("main");

  const [showMore, setShowMore] = useState(initialExpanded);

  const toggleSection = () => setShowMore((prev) => !prev);

  console.log("CarbonFeatureSection render", title, t(title));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <div
        style={{
          cursor: "pointer",
          width: "100%",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
        }}
        onClick={toggleSection}
      >
        {/* {icon} */}
        <Typography>{t(title)}</Typography>
        <Divider
          style={{
            flexGrow: "1",
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {showMore ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          overflow: "hidden",
          maxHeight: showMore ? (max_height ?? "300px") : "0px",
          transition: "max-height 0.5s ease",
          // padding: "1rem 0",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const AbsorptionLineChart = ({
  data,
  // clase,
  color = "#31a354",
}) => {
  const { t, i18n } = useTranslation("main");
  const { project } = useContext(AppContext);

  const clasesUnicas = useMemo(() => {
    const set = new Set(data?.map((row) => row.Clase));
    return Array.from(set).sort();
  }, [data]);

  const [selectedClase, setSelectedClase] = useState(clasesUnicas[0]);

  const handleChange = (e) => {
    setSelectedClase(e.target.value);
  };

  // Filtrar los datos de la clase específica
  const claseData = data
    ?.filter((item) => item.Clase === selectedClase)
    ?.sort((a, b) => a.Año - b.Año);

  if (!claseData || claseData.length === 0) {
    return (
      <p style={{ textAlign: "center" }}>
        {t("not_found")}: {selectedClase}
      </p>
    );
  }

  const chartData = {
    labels: claseData.map((row) => row.Año),
    datasets: [
      {
        label: selectedClase,
        data: claseData.map(
          (row) =>
            row[
              project.category === "CARBON_SINK_SP"
                ? "Absorción acumulada (tCO₂/km²)"
                : "Absorción acumulada (tCO2/ha)"
            ]
        ),
        borderColor: color,
        backgroundColor: `${color}33`, // con transparencia
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: t(`co2_absoption_evolution`),
        font: {
          size: 16,
        },
        color: "#333",
      },
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t("year"),
        },
      },
      y: {
        title: {
          display: true,
          text:
            project.category === "CARBON_SINK_SP"
              ? // ? "Absorción acumulada (tCO₂/km²)"
                // : "Absorción acumulada (tCO₂/ha)",
                t("accumulated_absorption_tCO2_km2")
              : t("accumulated_absorption_tCO2_ha"),
        },
        beginAtZero: true,
      },
    },
  };

  console.log("AbsorptionLineChart data", data);
  console.log("AbsorptionLineChart claseData", claseData);
  console.log("AbsorptionLineChart chartData", chartData);

  return (
    <>
      <TextField
        label={t("class")} // "Clase" traducido
        value={selectedClase}
        onChange={handleChange}
        select
        fullWidth
        style={{ margin: "1rem 0" }}
      >
        {clasesUnicas.map((clase) => (
          <MenuItem key={clase} value={clase}>
            {t(clase)}
          </MenuItem>
        ))}
      </TextField>
      <Line data={chartData} options={chartOptions} />
    </>
  );
};

const AbsorptionMultiLineChart = ({
  data,
  horizontes,
  colorMap = {},
  activeClassesData,
}) => {
  const { t } = useTranslation("main");

  const colHorizontes = useMemo(
    () => horizontes?.map((h) => `tCO2/km² 0-${h}`),
    [horizontes]
  );

  const clasesActivasIniciales = useMemo(() => {
    const set = new Set();
    activeClassesData?.forEach((row) => {
      if (row.Clase) set.add(row.Clase);
    });
    return Array.from(set);
  }, [activeClassesData]);

  const clasesDisponibles = useMemo(
    () =>
      data
        ?.map((d) => d.Clase)
        .filter(Boolean)
        .sort(),
    [data]
  );

  const [clasesSeleccionadas, setClasesSeleccionadas] = useState(() =>
    Object.fromEntries(
      clasesDisponibles.map((clase) => [
        clase,
        clasesActivasIniciales.includes(clase),
      ])
    )
  );

  const [openModal, setOpenModal] = useState(false);

  const handleToggleClase = (clase) => {
    setClasesSeleccionadas((prev) => ({
      ...prev,
      [clase]: !prev[clase],
    }));
  };

  const chartData = {
    labels: horizontes,
    datasets: data
      ?.filter((row) => clasesSeleccionadas[row.Clase])
      .map((row) => {
        const clase = row["Clase"];
        const color = colorMap[clase] || "#888";

        return {
          label: t(clase),
          data: colHorizontes.map((col) => row[col] ?? 0),
          borderColor: color,
          backgroundColor: `${color}33`,
          fill: false,
          tension: 0.2,
          pointRadius: 3,
        };
      }),
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: t("accumulated_carbon_by_class"),
        font: { size: 16 },
        color: "#333",
      },
      legend: {
        position: "bottom",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t("year"),
        },
      },
      y: {
        title: {
          display: true,
          text: "tCO₂/km²",
        },
        beginAtZero: true,
      },
    },
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    maxHeight: "80vh",
    overflowY: "hidden",
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    padding: "1rem 0",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "3rem",
              padding: "0 1rem",
              paddingBottom: "1rem",
            }}
          >
            <Typography>{t("select_classes")}</Typography>
            <IconButton
              style={{ margin: "-8px" }}
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <Divider />
          <FormGroup
            style={{ flexWrap: "nowrap", overflow: "auto", padding: "1rem" }}
          >
            {clasesDisponibles.map((clase) => (
              <FormControlLabel
                key={clase}
                control={
                  <Checkbox
                    checked={clasesSeleccionadas[clase]}
                    onChange={() => handleToggleClase(clase)}
                    style={{ color: colorMap[clase] || "#888" }}
                  />
                }
                label={t(clase)}
              />
            ))}
          </FormGroup>
        </Box>
      </Modal>

      <div
        style={{
          height: "300px",
          width: "100%",
        }}
      >
        <Line data={chartData} options={chartOptions} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={() => setOpenModal(true)}>
          {t("select_classes")}
        </Button>
      </div>
    </div>
  );
};

const AbsorptionScatterChart = ({
  data,
  densidadesBase,
  colorMap = {},
  activeClassesData,
}) => {
  const { t } = useTranslation("main");
  const { project } = useContext(AppContext);

  // Obtener clases activas a partir del segundo dataframe
  const clasesActivasIniciales = useMemo(() => {
    const set = new Set();
    activeClassesData?.forEach((row) => {
      if (row.Clase) set.add(row.Clase);
    });
    return Array.from(set);
  }, [activeClassesData]);

  // Agrupar puntos por clase (con absorción por pie)
  const dataByClass = useMemo(() => {
    const grouped = {};

    data?.forEach((row) => {
      const clase = row.Clase;
      const densidad = densidadesBase?.[clase];
      if (!densidad || densidad === 0) return;

      const absorcionKm2 =
        project.category === "CARBON_SINK_SP"
          ? row["Absorción acumulada (tCO₂/km²)"]
          : row["Absorción acumulada (tCO2/ha)"] * 100;
      const absorcionPie = absorcionKm2 / (densidad * 100);
      if (!grouped[clase]) grouped[clase] = [];
      grouped[clase].push({ x: absorcionPie, y: absorcionKm2 });
    });

    return grouped;
  }, [data, densidadesBase]);

  const clasesDisponibles = useMemo(
    () => Object.keys(dataByClass).sort(),
    [dataByClass]
  );

  const [clasesSeleccionadas, setClasesSeleccionadas] = useState(() =>
    Object.fromEntries(
      clasesDisponibles.map((clase) => [
        clase,
        clasesActivasIniciales.includes(clase),
      ])
    )
  );

  const [openModal, setOpenModal] = useState(false);
  const handleToggleClase = (clase) => {
    setClasesSeleccionadas((prev) => ({
      ...prev,
      [clase]: !prev[clase],
    }));
  };

  const chartData = {
    datasets: clasesDisponibles
      .filter((clase) => clasesSeleccionadas[clase])
      .map((clase) => {
        const puntos = dataByClass[clase];
        const color = colorMap?.[clase] || "#888";
        return {
          label: t(clase),
          data: puntos,
          backgroundColor: `${color}66`,
          borderColor: color,
          pointRadius: 4,
        };
      }),
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: false,
        text: t("relation_tCO2/pie_tCO2/km2"),
        font: { size: 16 },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t("accumulated_absorption_tCO2_tree"),
        },
      },
      y: {
        title: {
          display: true,
          text: t("accumulated_absorption_tCO2_km2"),
        },
        min: 0,
      },
    },
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    maxHeight: "80vh",
    overflowY: "hidden",
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    // p: 4,
    padding: "1rem 0",
    display: "flex",
    flexDirection: "column",
  };

  // console.log("AbsorptionScatterChart data", data);
  // console.log(
  //   "AbsorptionScatterChart clasesActivasIniciales",
  //   clasesActivasIniciales
  // );
  // console.log("AbsorptionScatterChart activeClassesData", activeClassesData);
  // console.log("AbsorptionScatterChart dataByClass", dataByClass);
  // console.log("AbsorptionScatterChart clasesDisponibles", clasesDisponibles);
  // console.log("AbsorptionScatterChart chartData", chartData);

  return (
    <div
      style={{
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "3rem",
              padding: "0 1rem",
              paddingBottom: "1rem",
              position: "relative",
            }}
          >
            {t("select_classes")}
            <IconButton
              style={{
                margin: "-8px",
              }}
              onClick={() => setOpenModal(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <Divider />
          <FormGroup
            style={{ flexWrap: "nowrap", overflow: "auto", padding: "1rem" }}
          >
            {clasesDisponibles.map((clase) => (
              <FormControlLabel
                key={clase}
                control={
                  <Checkbox
                    checked={clasesSeleccionadas[clase]}
                    onChange={() => handleToggleClase(clase)}
                    style={{ color: colorMap[clase] || "#888" }}
                  />
                }
                label={t(clase)}
              />
            ))}
          </FormGroup>
        </Box>
      </Modal>

      <div
        style={{
          width: "100%",
          maxHeight: "600px",
          minHeight: "300px",
          position: "relative",
        }}
      >
        <Scatter data={chartData} options={chartOptions} />
      </div>
      <Button
        variant="contained"
        onClick={() => setOpenModal(true)}
        sx={{ mb: 2, alignSelf: "flex-end" }}
      >
        {t("select_classes")}
      </Button>
    </div>
  );
};

const CarbonCreditsProjectionChart = ({ data }) => {
  const { t } = useTranslation("main");

  // Agrupar por año y sumar los tres escenarios
  const grouped = {};

  data.forEach((row) => {
    const año = row["Año"];
    if (!grouped[año]) {
      grouped[año] = {
        constante: 0,
        optimista: 0,
        pesimista: 0,
      };
    }

    grouped[año].constante +=
      row["carbon_credits_tCO2e_acumulados_constante (€)"] || 0;
    grouped[año].optimista +=
      row["carbon_credits_tCO2e_acumulados_optimista (€)"] || 0;
    grouped[año].pesimista +=
      row["carbon_credits_tCO2e_acumulados_pesimista (€)"] || 0;
  });

  const años = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b);
  const getSerie = (tipo) => años.map((año) => grouped[año][tipo]);

  const chartData = {
    labels: años,
    datasets: [
      {
        label: t("constant_scenario"),
        data: getSerie("constante"),
        borderColor: "#1f78b4",
        backgroundColor: "#1f78b455",
        pointRadius: 4,
      },
      {
        label: t("optimistic_scenario"),
        data: getSerie("optimista"),
        borderColor: "#33a02c",
        backgroundColor: "#33a02c55",
        pointRadius: 4,
      },
      {
        label: t("pessimistic_scenario"),
        data: getSerie("pesimista"),
        borderColor: "#e31a1c",
        backgroundColor: "#e31a1c55",
        pointRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: t("carbon_credit_projection_full_area"),
        font: { size: 16 },
      },
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t("year"),
        },
      },
      y: {
        title: {
          display: true,
          text: "€",
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div
      style={{
        height: "400px",
        width: "100%",
      }}
    >
      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

const ClassCreditsHorizonChart = ({ data }) => {
  const { t } = useTranslation("main");

  const years = [5, 10, 15, 20, 25, 30, 35, 40];

  // Filtrar años y agrupar
  const resumen = useMemo(() => {
    const filtered = data.filter((row) => years.includes(row.Año));
    const grouped = {};

    filtered.forEach((row) => {
      const clase = row.Clase;
      if (!grouped[clase]) grouped[clase] = {};

      const year = row.Año;
      grouped[clase][year] = {
        constante: row["carbon_credits_tCO2e_acumulados_constante (€)"] || 0,
        optimista: row["carbon_credits_tCO2e_acumulados_optimista (€)"] || 0,
        pesimista: row["carbon_credits_tCO2e_acumulados_pesimista (€)"] || 0,
      };
    });

    return grouped;
  }, [data]);

  const clasesDisponibles = Object.keys(resumen).sort();
  const [selectedClase, setSelectedClase] = useState(clasesDisponibles[0]);

  const handleClaseChange = (e) => {
    setSelectedClase(e.target.value);
  };

  const valores = resumen[selectedClase];

  const chartData = {
    labels: years,
    datasets: [
      {
        label: t("constant_scenario"),
        data: years.map((y) => valores?.[y]?.constante ?? 0),
        borderColor: "green",
        backgroundColor: "rgba(0,128,0,0.2)",
        borderWidth: 2,
      },
      {
        label: t("optimistic_scenario"),
        data: years.map((y) => valores?.[y]?.optimista ?? 0),
        borderColor: "blue",
        backgroundColor: "rgba(0,0,255,0.2)",
        borderWidth: 2,
        borderDash: [5, 5],
      },
      {
        label: t("pessimistic_scenario"),
        data: years.map((y) => valores?.[y]?.pesimista ?? 0),
        borderColor: "red",
        backgroundColor: "rgba(255,0,0,0.2)",
        borderWidth: 2,
        borderDash: [2, 4],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: `${t("carbon_credits_per_class")} - ${t(selectedClase)}`,
        font: { size: 16 },
      },
      legend: { position: "top" },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `${context.dataset.label}: € ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t("year") },
      },
      y: {
        title: { display: true, text: "€" },
        beginAtZero: true,
      },
    },
  };

  return (
    <div style={{ width: "100%", margin: "auto" }}>
      <TextField
        select
        fullWidth
        label={t("class")}
        value={selectedClase}
        onChange={handleClaseChange}
        style={{ margin: "1rem 0" }}
      >
        {clasesDisponibles.map((clase) => (
          <MenuItem key={clase} value={clase}>
            {t(clase)}
          </MenuItem>
        ))}
      </TextField>

      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

const CreditsPerKm2ByClassChart = ({ data, colorMap = {} }) => {
  const { t } = useTranslation("main");

  const horizontes = [5, 10, 15, 20, 25, 30, 35, 40];

  const escenarios = {
    constante: "carbon_credits_tCO2e_acumulados_constante €/km²",
    optimista: "carbon_credits_tCO2e_acumulados_optimista €/km²",
    pesimista: "carbon_credits_tCO2e_acumulados_pesimista €/km²",
  };

  const [escenario, setEscenario] = useState("constante");

  const handleChange = (e) => {
    setEscenario(e.target.value);
  };

  const chartData = useMemo(() => {
    const col = escenarios[escenario];
    const clasesUnicas = Array.from(new Set(data.map((d) => d.Clase)));

    const datasets = clasesUnicas.map((clase) => {
      const valores = data
        .filter((d) => d.Clase === clase && horizontes.includes(d.Año))
        .map((d) => ({ año: d.Año, valor: d[col] ?? 0 }))
        .sort((a, b) => a.año - b.año);

      if (valores.length === 0 || valores.every((v) => !v.valor)) return null;

      return {
        label: t(clase),
        data: valores.map((v) => v.valor),
        borderColor: colorMap?.[clase] || "#888",
        backgroundColor: (colorMap?.[clase] || "#888") + "33",
        tension: 0.2,
        pointRadius: 3,
      };
    });

    return {
      labels: horizontes,
      datasets: datasets.filter(Boolean), // quitar clases vacías
    };
  }, [data, escenario, colorMap]);

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: `${t("accumulated_credits_per_km2")} - ${t(escenario)} ${t("scenario")}`,
        font: { size: 16 },
      },
      legend: {
        position: "bottom",
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `${context.dataset.label}: €/${"km²"} ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t("year") },
      },
      y: {
        title: { display: true, text: "€ / km²" },
        beginAtZero: true,
      },
    },
  };

  return (
    <div style={{ width: "100%", margin: "auto" }}>
      <TextField
        select
        label={t("scenario")}
        value={escenario}
        onChange={handleChange}
        fullWidth
        style={{ margin: "1rem 0" }}
      >
        {Object.keys(escenarios).map((key) => (
          <MenuItem key={key} value={key}>
            {t(`${key}_scenario`)}
          </MenuItem>
        ))}
      </TextField>

      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

const estadoLabels = {
  1: "Repoblado joven (3 años)",
  2: "Repoblado medio (7 años)",
  3: "Repoblado avanzado (13 años)",
  4: "Madura (25 años)",
  default: "Desconocido",
};

const VegetationStateAreaChart = ({ data }) => {
  const { t } = useTranslation("main");

  const processed = useMemo(() => {
    const grouped = {};

    data.forEach((row) => {
      const tipo = row.Tipo_especie || "Desconocido";
      const ff_e = row.FF_E;
      const estado = estadoLabels[ff_e] || estadoLabels.default;
      const area = row.area_ha || 0;

      if (!grouped[tipo]) {
        grouped[tipo] = {
          "Repoblado joven (3 años)": 0,
          "Repoblado medio (7 años)": 0,
          "Repoblado avanzado (13 años)": 0,
          "Madura (25 años)": 0,
          Desconocido: 0,
        };
      }

      grouped[tipo][estado] += area;
    });

    const especiesOrdenadas = Object.entries(grouped)
      .map(([tipo, valores]) => ({
        tipo,
        total: Object.values(valores).reduce((a, b) => a + b, 0),
      }))
      .sort((a, b) => a.total - b.total)
      .map((item) => item.tipo);

    const estados = [
      "Repoblado joven (3 años)",
      "Repoblado medio (7 años)",
      "Repoblado avanzado (13 años)",
      "Madura (25 años)",
      "Desconocido",
    ];

    const datasets = estados.map((estado, idx) => ({
      label: estado,
      data: especiesOrdenadas.map((tipo) => grouped[tipo][estado] || 0),
      backgroundColor: ["#8dd3c7", "#ffffb3", "#bebada", "#fb8072", "#d9d9d9"][
        idx
      ],
    }));

    return {
      labels: especiesOrdenadas.map((tipo) => t(tipo)),
      datasets,
    };
  }, [data, t]);

  const options = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 16,
        },
      },
      title: {
        display: false,
        text:
          t("vegetation_state_distribution_title") ||
          "Distribución de estados vegetativos por tipo de cobertura en el AOI para el año 2025",
        font: { size: 16 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            return `${ctx.dataset.label}: ${ctx.raw.toFixed(2)} ha`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: t("total_area_ha"),
        },
        ticks: { beginAtZero: true },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: t("class") || "Tipo de cobertura",
        },
      },
    },
  };

  return (
    <div
      style={{
        height: "400px",
        width: "100%",
      }}
    >
      <Bar data={processed} options={options} />
    </div>
  );
};

const CreditScenarioLineChartWithSelector = ({ data, colorMap = {} }) => {
  const { t } = useTranslation("main");

  const escenarios = {
    constante: "carbon_credits_tCO2e_acumulados_constante",
    optimista: "carbon_credits_tCO2e_acumulados_optimista",
    pesimista: "carbon_credits_tCO2e_acumulados_pesimista",
  };

  const [selectedScenario, setSelectedScenario] = useState("constante");
  const escenarioCol = escenarios[selectedScenario];

  // Agrupar datos por clase y ordenar por año
  const chartData = useMemo(() => {
    const grouped = {};

    data?.forEach((row) => {
      const clase = row.Clase;
      const año = row.Año;
      const valor = row[escenarioCol];

      if (clase != null && año != null && valor != null && !isNaN(valor)) {
        if (!grouped[clase]) grouped[clase] = [];
        grouped[clase].push({ x: año, y: valor });
      }
    });

    // Aseguramos que cada grupo esté ordenado por año
    const datasets = Object.entries(grouped).map(([clase, puntos]) => {
      puntos.sort((a, b) => a.x - b.x);

      return {
        label: t(clase),
        data: puntos,
        borderColor: colorMap[clase] || "#888",
        backgroundColor: (colorMap[clase] || "#888") + "33",
        fill: false,
        tension: 0.2,
        pointRadius: 4,
        borderWidth: 2,
      };
    });

    return { datasets };
  }, [data, escenarioCol, colorMap, t]);

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: t("accumulated_credits_by_class"),
        font: { size: 16 },
      },
      legend: {
        position: "right",
        labels: { usePointStyle: true },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.raw?.y ?? 0;
            return `${context.dataset.label}: € ${value.toLocaleString(
              undefined,
              {
                maximumFractionDigits: 2,
              }
            )}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: t("year"),
        },
        type: "linear",
        ticks: { precision: 0 },
      },
      y: {
        title: {
          display: true,
          text: "€",
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <>
      <TextField
        select
        fullWidth
        label={t("select_scenario")}
        value={selectedScenario}
        onChange={(e) => setSelectedScenario(e.target.value)}
        style={{ margin: "1rem 0" }}
      >
        {Object.keys(escenarios).map((key) => (
          <MenuItem key={key} value={key}>
            {t(key + "_scenario")}
          </MenuItem>
        ))}
      </TextField>

      <Line data={chartData} options={chartOptions} />
    </>
  );
};

const CreditByClassMultiChart = ({ data, colorMap = {} }) => {
  const { t } = useTranslation("main");

  const clasesDisponibles = useMemo(
    () => Array.from(new Set(data?.map((row) => row.Clase))).sort(),
    [data]
  );

  const [selectedClase, setSelectedClase] = useState(clasesDisponibles[0]);

  const filteredData = useMemo(
    () => data?.filter((row) => row.Clase === selectedClase),
    [data, selectedClase]
  );

  const chartData = useMemo(() => {
    return {
      labels: filteredData?.map((row) => row.Año),
      datasets: [
        {
          label: t("constant_scenario"),
          data: filteredData?.map(
            (row) => row["carbon_credits_tCO2e_acumulados_constante"] ?? 0
          ),
          borderColor: "green",
          backgroundColor: "rgba(0,128,0,0.2)",
          borderWidth: 2,
        },
        {
          label: t("optimistic_scenario"),
          data: filteredData?.map(
            (row) => row["carbon_credits_tCO2e_acumulados_optimista"] ?? 0
          ),
          borderColor: "blue",
          backgroundColor: "rgba(0,0,255,0.2)",
          borderWidth: 2,
          borderDash: [5, 5],
        },
        {
          label: t("pessimistic_scenario"),
          data: filteredData?.map(
            (row) => row["carbon_credits_tCO2e_acumulados_pesimista"] ?? 0
          ),
          borderColor: "red",
          backgroundColor: "rgba(255,0,0,0.2)",
          borderWidth: 2,
          borderDash: [2, 4],
        },
      ],
    };
  }, [filteredData, t]);

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: t("carbon_credits_per_class"),
        font: { size: 16 },
      },
      legend: { position: "top" },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `${context.dataset.label}: € ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t("year") },
      },
      y: {
        title: { display: true, text: "€" },
        beginAtZero: true,
      },
    },
  };

  return (
    <>
      <TextField
        select
        fullWidth
        label={t("class")}
        value={selectedClase}
        onChange={(e) => {
          setSelectedClase(e.target.value);
        }}
        style={{ margin: "1rem 0" }}
      >
        {clasesDisponibles.map((clase) => (
          <MenuItem key={clase} value={clase}>
            {t(clase)}
          </MenuItem>
        ))}
      </TextField>
      <div style={{ height: "400px", marginBottom: "1rem" }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </>
  );
};

export default function DownloadDocButton({ country = "spain", lang = "en" }) {
  const handleDownload = async () => {
    // try {
    //   const url = `${process.env.REACT_APP_API_URL}layers/docs/carbon_sink/${encodeURIComponent(country)}/${encodeURIComponent(lang)}`;
    //   const res = await fetch(url, {
    //     method: "GET",
    //     credentials: "include", // si usas cookies con auth_required
    //   });

    //   if (!res.ok) {
    //     alert("No se pudo descargar el documento");
    //     return;
    //   }

    //   const blob = await res.blob();
    //   const href = URL.createObjectURL(blob);

    //   const a = document.createElement("a");
    //   a.href = href;
    //   a.download = `carbon_sink_methodology_${country}_${lang}.pdf`; // nombre sugerido
    //   document.body.appendChild(a);
    //   a.click();
    //   a.remove();
    //   URL.revokeObjectURL(href);
    // } catch (error) {
    //   console.error("Error al descargar el documento:", error);
    //   alert("Error al descargar el documento");
    // }

    try {
      const { blob, filename } = await downloadCarbonSinkDoc(country, lang);
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename || `carbon_sink_methodology_${country}_${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      console.error("Error al descargar el documento:", err);
      alert("No se pudo descargar el documento");
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<DownloadIcon />}
      onClick={handleDownload}
    >
      Descargar ({country}, {lang.toUpperCase()})
    </Button>
  );
}
